import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { COLORS } from '../constants/theme';
import { copyTextToClipboard } from '../utils/copyToClipboard';

interface ProductShareModalProps {
  visible: boolean;
  shareUrl: string;
  onClose: () => void;
}

export const ProductShareModal: React.FC<ProductShareModalProps> = ({
  visible,
  shareUrl,
  onClose,
}) => {
  const [isCopying, setIsCopying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!visible) {
      setCopied(false);
      setIsCopying(false);
    }
  }, [visible]);

  const handleCopy = async () => {
    if (!shareUrl || isCopying) {
      return;
    }

    try {
      setIsCopying(true);
      await copyTextToClipboard(shareUrl);
      setCopied(true);
    } catch {
      if (Platform.OS === 'web') {
        window.alert('Could not copy the link. Please copy it manually.');
      }
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.content}>
          <Text style={styles.title}>Share product</Text>
          <Text style={styles.description}>
            Anyone with this link can view this product in read-only mode.
          </Text>

          <View style={styles.urlBox}>
            <Text style={styles.urlText} selectable>
              {shareUrl}
            </Text>
          </View>

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.copyButton, copied && styles.copyButtonSuccess]}
              onPress={handleCopy}
              disabled={isCopying}
              activeOpacity={0.7}
            >
              {isCopying ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.copyButtonText}>{copied ? 'Copied' : 'Copy'}</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity style={styles.closeButton} onPress={onClose} activeOpacity={0.7}>
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 16,
    lineHeight: 20,
  },
  urlBox: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 12,
    marginBottom: 20,
  },
  urlText: {
    fontSize: 13,
    color: '#111827',
    lineHeight: 18,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  copyButton: {
    minWidth: 96,
    backgroundColor: COLORS.primary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  copyButtonSuccess: {
    backgroundColor: '#059669',
  },
  copyButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  closeButton: {
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#F3F4F6',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  closeButtonText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '600',
  },
});
